/**
 * Toast Manager for Redux Middleware
 *
 * This module provides a way for Redux middleware to trigger toast notifications
 * without direct access to React context. The ToastProvider registers its methods
 * here, and middleware can call these methods.
 */

interface ToastManager {
  showWarning: ((message: string, duration?: number) => void) | null;
  showInfo: ((message: string, duration?: number) => void) | null;
  showError: ((message: string, duration?: number) => void) | null;
  showSuccess: ((message: string, duration?: number) => void) | null;
}

interface QueuedToast {
  showFn: (message: string, duration?: number) => void;
  message: string;
  duration: number;
}
const toastManager: ToastManager = {
  showWarning: null,
  showInfo: null,
  showError: null,
  showSuccess: null,
};

// Global toast queue to ensure only one toast shows at a time
const globalToastQueue: QueuedToast[] = [];
let isShowingToast = false;
let currentToastMessage: string | null = null;

// Process the global toast queue - show one toast at a time
const processGlobalToastQueue = () => {
  console.log('🔄 processGlobalToastQueue:', { isShowingToast, queueLength: globalToastQueue.length });
  if (isShowingToast || globalToastQueue.length === 0) {
    console.log('⏸️ Not processing:', isShowingToast ? 'already showing' : 'queue empty');
    return; // Already showing a toast or queue is empty
  }
  
  const nextToast = globalToastQueue.shift();
  if (!nextToast) return;
  
  console.log('🎬 Showing toast:', nextToast.message);
  isShowingToast = true;
  currentToastMessage = nextToast.message;
  nextToast.showFn(nextToast.message, nextToast.duration);
  
  // Set timer to mark toast as done and process next
  setTimeout(() => {
    isShowingToast = false;
    currentToastMessage = null;
    processGlobalToastQueue(); // Process next toast in queue
  }, nextToast.duration + 500); // Add 500ms gap between toasts
};

// Helper function to queue toast messages globally
const queueGlobalToast = (showFn: (msg: string, duration: number) => void, message: string, duration = 5000) => {
  console.log('📬 queueGlobalToast called:', { message, hasShowFn: !!showFn });
  if (!showFn) {
    console.error('❌ No showFn provided to queueGlobalToast');
    return;
  }
  
  // Check for duplicate messages in queue or currently showing to prevent spam
  const isDuplicate = globalToastQueue.some(toast => toast.message === message) || 
                      currentToastMessage === message;
  if (isDuplicate) {
    console.log('🔍 Skipping duplicate toast:', message);
    return; // Skip duplicate messages
  }
  
  // Add to global queue
  globalToastQueue.push({ showFn, message, duration });
  console.log('📥 Added to queue. Queue length:', globalToastQueue.length);
  
  // Process queue if not already showing a toast
  processGlobalToastQueue();
};
/**
 * Register toast functions from the ToastProvider
 */
export const registerToastMethods = (methods: {
  showWarning: (message: string, duration?: number) => void;
  showInfo: (message: string, duration?: number) => void;
  showError: (message: string, duration?: number) => void;
  showSuccess: (message: string, duration?: number) => void;
}) => {
  toastManager.showWarning = methods.showWarning;
  toastManager.showInfo = methods.showInfo;
  toastManager.showError = methods.showError;
  toastManager.showSuccess = methods.showSuccess;
};

/**
 * Show a warning toast from middleware (queued globally)
 */
export const showWarning = (message: string, duration = 5000) => {
  console.log('🔔 showWarning called:', message, 'Manager initialized:', !!toastManager.showWarning);
  if (toastManager.showWarning) {
    queueGlobalToast(toastManager.showWarning, message, duration);
  } else {
    console.warn('Toast manager not initialized:', message);
  }
};

/**
 * Show an info toast from middleware (queued globally)
 */
export const showInfo = (message: string, duration = 3000) => {
  if (toastManager.showInfo) {
    queueGlobalToast(toastManager.showInfo, message, duration);
  } else {
    console.info('Toast manager not initialized:', message);
  }
};

/**
 * Show an error toast from middleware (queued globally)
 */
export const showError = (message: string, duration = 6000) => {
  console.log('🔔 showError called:', message, 'Manager initialized:', !!toastManager.showError);
  if (toastManager.showError) {
    queueGlobalToast(toastManager.showError, message, duration);
  } else {
    console.error('Toast manager not initialized:', message);
  }
};

/**
 * Show a success toast from middleware (queued globally)
 */
export const showSuccess = (message: string, duration = 3000) => {
  if (toastManager.showSuccess) {
    queueGlobalToast(toastManager.showSuccess, message, duration);
  } else {
    console.log('Toast manager not initialized:', message);
  }
};

export default {
  registerToastMethods,
  showWarning,
  showInfo,
  showError,
  showSuccess,
};
